import { IPublicClientApplication } from '@azure/msal-browser';
import { apiScopes, graphScopes } from '../../../config/authConfig';

export class EntraService {
  private msalInstance: IPublicClientApplication;
  private graphEndpoint = 'https://graph.microsoft.com/v1.0';

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  private async getAccessToken(scopes: string[] = graphScopes): Promise<string> {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No authenticated user found');
    }

    const request = {
      scopes: scopes,
      account: accounts[0]
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (silentError: any) {
      console.warn('Silent token acquisition failed, attempting interactive login:', silentError);
      
      // Check if there's already an interaction in progress
      if (silentError?.errorCode === 'interaction_in_progress') {
        throw new Error('Authentication in progress. Please wait and try again.');
      }
      
      try {
        // Try popup as fallback
        const response = await this.msalInstance.acquireTokenPopup(request);
        return response.accessToken;
      } catch (popupError: any) {
        console.error('Popup authentication failed:', popupError);
        
        // If popup is blocked, try redirect
        if (popupError?.errorCode === 'popup_window_error') {
          console.log('Popup blocked, using redirect method');
          await this.msalInstance.acquireTokenRedirect(request);
          // This will redirect the page, so we won't reach here
          throw new Error('Redirecting for authentication...');
        }
        
        throw popupError;
      }
    }
  }

  private async getGraphAccessToken(): Promise<string> {
    return this.getAccessToken(graphScopes);
  }

  private async getApiAccessToken(): Promise<string> {
    return this.getAccessToken(apiScopes);
  }

  async getUserInfo(): Promise<{ id: string; name: string; email: string }> {
    try {
      const token = await this.getGraphAccessToken();
      const response = await fetch(`${this.graphEndpoint}/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user info');
      }

      const user = await response.json();
      return {
        id: user.id,
        name: user.displayName || user.userPrincipalName,
        email: user.mail || user.userPrincipalName
      };
    } catch (error) {
      console.error('Failed to get user info:', error);
      throw new Error('Failed to get user information');
    }
  }

  private async hasProfilePicture(): Promise<boolean> {
    try {
      const token = await this.getGraphAccessToken();
      const metadataResponse = await fetch(`${this.graphEndpoint}/me/photo`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      return metadataResponse.ok;
    } catch (error) {
      return false;
    }
  }

  async getProfilePicture(): Promise<string | null> {
    try {
      // First check if user has a profile picture to avoid unnecessary 404s
      const hasPicture = await this.hasProfilePicture();
      if (!hasPicture) {
        return null;
      }

      const token = await this.getGraphAccessToken();
      const photoResponse = await fetch(`${this.graphEndpoint}/me/photo/$value`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!photoResponse.ok) {
        if (photoResponse.status === 404) {
          return null;
        }
        throw new Error(`HTTP ${photoResponse.status}: Failed to get profile picture`);
      }

      const blob = await photoResponse.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error: any) {
      // Silently handle missing profile pictures
      if (error?.status === 404 || 
          error.message?.includes('404') || 
          error.message?.includes('Not Found')) {
        return null;
      }
      
      console.error('Failed to get profile picture:', error);
      throw new Error('Failed to load profile picture');
    }
  }

  async uploadProfilePicture(imageFile: File): Promise<void> {
    try {
      // Validate file size (max 4MB for Graph API)
      if (imageFile.size > 4 * 1024 * 1024) {
        throw new Error('Image size must be less than 4MB');
      }

      // Get user token for authentication with our backend
      const token = await this.getApiAccessToken();
      
      // Get user info to identify which user's picture to update
      const userInfo = await this.getUserInfo();
      
      // Create FormData to send to backend
      const formData = new FormData();
      formData.append('file', imageFile);
      formData.append('userId', userInfo.id);
      
      // Send to backend API which has ProfilePhoto.ReadWrite.All app permission
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/enhancer/profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 413) {
          throw new Error('Image size is too large. Please use an image smaller than 4MB.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to update your profile picture. Please contact your administrator.');
        }
        throw new Error('Failed to upload profile picture');
      }
      
      // Note: It may take a few moments for the photo to propagate
      // across all Microsoft services
    } catch (error: any) {
      console.error('Failed to upload profile picture:', error);
      
      if (error.message?.includes('Network')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      throw error;
    }
  }

  async deleteProfilePicture(): Promise<void> {
    try {
      // Get user token for authentication with our backend
      const token = await this.getApiAccessToken();
      const userInfo = await this.getUserInfo();
      
      // Delete via backend which has ProfilePhoto.ReadWrite.All app permission
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/enhancer/profile-picture/${userInfo.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok && response.status !== 404) {
        if (response.status === 403) {
          throw new Error('Unable to delete profile picture. Please contact your administrator.');
        }
        throw new Error('Failed to delete profile picture');
      }
    } catch (error: any) {
      console.error('Failed to delete profile picture:', error);
      throw error;
    }
  }

  async getProfilePictureUrl(): Promise<string | null> {
    try {
      // This returns a URL that can be used directly in an img tag
      // Note: This requires the user to be authenticated
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        return null;
      }

      // Get access token to ensure we're authenticated
      await this.getGraphAccessToken();
      
      // Return the Graph API photo endpoint with auth header
      // This is a simpler approach for displaying the image
      return `https://graph.microsoft.com/v1.0/me/photo/$value`;
    } catch (error) {
      console.error('Failed to get profile picture URL:', error);
      return null;
    }
  }

  // Helper method to check if user has permission to modify profile
  async checkProfilePermissions(): Promise<boolean> {
    try {
      const token = await this.getGraphAccessToken();
      const response = await fetch(`${this.graphEndpoint}/me/memberOf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Check if user has necessary permissions
      // This is a simplified check - you may need to adjust based on your org's setup
      return response.ok;
    } catch (error) {
      console.error('Failed to check permissions:', error);
      return false;
    }
  }

  // Profile persistence methods for Entra extension attributes
  async getProfile(): Promise<{ profile_markdown: string | null; original_length?: number } | null> {
    try {
      const token = await this.getApiAccessToken();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/enhancer/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error('Failed to get profile');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to get profile:', error);
      throw new Error('Failed to load profile from Entra');
    }
  }

  async saveProfile(profileMarkdown: string): Promise<void> {
    try {
      const token = await this.getApiAccessToken();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/enhancer/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          profile_markdown: profileMarkdown
        })
      });

      if (!response.ok) {
        if (response.status === 413) {
          throw new Error('Profile is too large to save. Please reduce the content size.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to save your profile. Please contact your administrator.');
        }
        throw new Error('Failed to save profile');
      }
    } catch (error: any) {
      console.error('Failed to save profile:', error);
      
      if (error.message?.includes('Network')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      throw error;
    }
  }

  async deleteProfile(): Promise<void> {
    try {
      const token = await this.getApiAccessToken();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/enhancer/profile`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok && response.status !== 404) {
        if (response.status === 403) {
          throw new Error('You do not have permission to delete your profile. Please contact your administrator.');
        }
        throw new Error('Failed to delete profile');
      }
    } catch (error: any) {
      console.error('Failed to delete profile:', error);
      
      if (error.message?.includes('Network')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      throw error;
    }
  }
}
